import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, FloppyDisk, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const selectCls =
  "w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400/50 disabled:opacity-50";

export const QboMapping = ({ connected }) => {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [taxcodes, setTaxcodes] = useState([]);
  const [lines, setLines] = useState({});
  const [tax, setTax] = useState({ code_id: "", code_name: "", apply: false });
  const [numberMode, setNumberMode] = useState("quickbooks");
  const [refApp, setRefApp] = useState(true);
  const [shipAddr, setShipAddr] = useState(true);
  const [materialDesc, setMaterialDesc] = useState(true);
  const [cogsAccounts, setCogsAccounts] = useState([]);
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [deposit, setDeposit] = useState({ id: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [creatingItem, setCreatingItem] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/qbo/mapping");
      setData(d);
      setLines(d.lines || {});
      setTax(d.tax || { code_id: "", code_name: "", apply: false });
      setNumberMode(d.invoice_number_mode || "quickbooks");
      setRefApp(d.reference_app_invoice !== false);
      setShipAddr(d.ship_addr_from_job !== false);
      setMaterialDesc(d.material_in_description !== false);
      setDeposit({ id: d.deposit_account_id || "", name: d.deposit_account_name || "" });
      if (d.connected) {
        const [it, ac, cg, tx, dep] = await Promise.all([
          api.get("/qbo/items").catch(() => ({ data: { items: [] } })),
          api.get("/qbo/accounts").catch(() => ({ data: { accounts: [] } })),
          api.get("/qbo/cogs-accounts").catch(() => ({ data: { accounts: [] } })),
          api.get("/qbo/taxcodes").catch(() => ({ data: { taxcodes: [] } })),
          api.get("/qbo/deposit-accounts").catch(() => ({ data: { accounts: [] } })),
        ]);
        setItems(it.data.items || []);
        setAccounts(ac.data.accounts || []);
        setCogsAccounts(cg.data.accounts || []);
        setTaxcodes(tx.data.taxcodes || []);
        setDepositAccounts(dep.data.accounts || []);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load mapping");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setLine = (cat, patch) =>
    setLines((prev) => ({ ...prev, [cat]: { ...(prev[cat] || {}), ...patch } }));

  const onItem = (cat, id) => {
    const it = items.find((x) => String(x.id) === String(id));
    setLine(cat, { item_id: id, item_name: it?.name || "" });
  };
  const onAccount = (cat, id) => {
    const ac = accounts.find((x) => String(x.id) === String(id));
    setLine(cat, { account_id: id, account_name: ac?.name || "" });
  };
  const onCogs = (cat, id) => {
    const ac = cogsAccounts.find((x) => String(x.id) === String(id));
    setLine(cat, { cogs_account_id: id, cogs_account_name: ac?.name || "" });
  };
  const onSku = (cat, value) => setLine(cat, { sku: value });

  const createItem = async (c) => {
    const row = lines[c.id] || {};
    if (!row.account_id) {
      toast.error("Pick an income account first");
      return;
    }
    setCreatingItem(c.id);
    try {
      const { data: it } = await api.post("/qbo/items/create", {
        name: c.label,
        sku: row.sku || "",
        income_account_id: row.account_id,
        cogs_account_id: row.cogs_account_id || "",
      });
      setItems((prev) => [...prev.filter((x) => String(x.id) !== String(it.id)), it]);
      setLine(c.id, { item_id: String(it.id), item_name: it.name });
      toast.success(`Created "${it.name}" in QuickBooks`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create item");
    }
    setCreatingItem("");
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data: d } = await api.post("/qbo/mapping/suggest", {});
      setLines(d.lines || {});
      toast.success("AI filled in a suggested mapping — review and Save");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not suggest");
    }
    setSuggesting(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/qbo/mapping", {
        lines,
        tax,
        invoice_number_mode: numberMode,
        reference_app_invoice: refApp,
        ship_addr_from_job: shipAddr,
        material_in_description: materialDesc,
        deposit_account_id: deposit.id,
        deposit_account_name: deposit.name,
      });
      toast.success("Mapping saved");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save mapping");
    }
    setSaving(false);
  };

  if (!data) return null;
  const cats = data.categories || [];

  return (
    <div
      className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900 p-6"
      data-testid="qbo-mapping-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-zinc-100">Data mapping</h3>
          <p className="text-sm text-zinc-500">
            Send each charge type to the right QuickBooks item, income and COGS account
            for accurate profit tracking.
          </p>
        </div>
        <Button
          data-testid="qbo-map-ai-suggest"
          onClick={suggest}
          disabled={suggesting || !connected || !data.ai_ready}
          className="gap-2 bg-indigo-500 text-white hover:bg-indigo-400 disabled:opacity-40"
        >
          <Sparkle size={16} weight="bold" /> {suggesting ? "Thinking…" : "AI suggest"}
        </Button>
      </div>

      {!connected && (
        <div
          className="mt-4 flex items-center gap-2 rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-amber-200/90"
          data-testid="qbo-map-connect-note"
        >
          <Warning size={16} weight="bold" />
          Connect QuickBooks to load your items and accounts. Saved choices are shown below.
        </div>
      )}

      {/* Line mapping */}
      <div className="mt-5 space-y-3">
        <p className="px-1 text-[10px] uppercase tracking-widest text-zinc-500">
          Map each charge type to a QuickBooks item, income account, SKU and COGS account
        </p>
        {cats.map((c) => {
          const row = lines[c.id] || {};
          return (
            <div
              key={c.id}
              className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4"
              data-testid={`qbo-map-row-${c.id}`}
            >
              <div className="mb-3 min-w-0">
                <span className="block text-sm font-medium text-zinc-200">
                  {c.label}
                  {!c.active && (
                    <span className="ml-2 rounded bg-zinc-800 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-zinc-500">
                      future
                    </span>
                  )}
                </span>
                <span className="block truncate text-xs text-zinc-600">{c.desc}</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">
                    QuickBooks item
                  </span>
                  {connected ? (
                    <>
                      <select
                        data-testid={`qbo-map-item-${c.id}`}
                        value={row.item_id || ""}
                        onChange={(e) => onItem(c.id, e.target.value)}
                        className={selectCls}
                      >
                        <option value="">— Default item —</option>
                        {items.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name}
                          </option>
                        ))}
                      </select>
                      {!row.item_id && (
                        <button
                          data-testid={`qbo-map-create-item-${c.id}`}
                          onClick={() => createItem(c)}
                          disabled={creatingItem === c.id}
                          className="mt-1 text-[11px] font-semibold text-indigo-400 hover:underline disabled:opacity-40"
                        >
                          {creatingItem === c.id ? "Creating…" : "+ Create this item in QuickBooks"}
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="block truncate text-sm text-zinc-400" data-testid={`qbo-map-item-${c.id}`}>
                      {row.item_name || "Default item"}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">
                    Income account
                  </span>
                  {connected ? (
                    <select
                      data-testid={`qbo-map-account-${c.id}`}
                      value={row.account_id || ""}
                      onChange={(e) => onAccount(c.id, e.target.value)}
                      className={selectCls}
                    >
                      <option value="">— From item —</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className="block truncate text-sm text-zinc-400"
                      data-testid={`qbo-map-account-${c.id}`}
                    >
                      {row.account_name || "—"}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">
                    SKU / product code
                  </span>
                  <input
                    data-testid={`qbo-map-sku-${c.id}`}
                    value={row.sku || ""}
                    onChange={(e) => onSku(c.id, e.target.value)}
                    placeholder="e.g. FAB-INSTALL"
                    className={selectCls}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">
                    COGS / expense account
                  </span>
                  {connected ? (
                    <select
                      data-testid={`qbo-map-cogs-${c.id}`}
                      value={row.cogs_account_id || ""}
                      onChange={(e) => onCogs(c.id, e.target.value)}
                      className={selectCls}
                    >
                      <option value="">— None —</option>
                      {cogsAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className="block truncate text-sm text-zinc-400"
                      data-testid={`qbo-map-cogs-${c.id}`}
                    >
                      {row.cogs_account_name || "—"}
                    </span>
                  )}
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* Invoice numbering */}
      <div className="mt-6 border-t border-zinc-800 pt-5">
        <h4 className="text-sm font-semibold text-zinc-200">Invoice numbering</h4>
        <p className="mt-1 text-xs text-zinc-500">
          Choose which system owns the invoice number when a sale is pushed to
          QuickBooks.
        </p>
        <div className="mt-3 space-y-2">
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input
              data-testid="qbo-number-mode-quickbooks"
              type="radio"
              name="qbo-number-mode"
              checked={numberMode === "quickbooks"}
              onChange={() => setNumberMode("quickbooks")}
              className="mt-1 h-4 w-4 accent-indigo-500"
            />
            <span>
              <span className="font-medium text-zinc-200">
                Let QuickBooks assign the next number
              </span>
              <span className="block text-xs text-zinc-500">
                Recommended — keeps QuickBooks' own invoice sequence unbroken.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input
              data-testid="qbo-number-mode-app"
              type="radio"
              name="qbo-number-mode"
              checked={numberMode === "app"}
              onChange={() => setNumberMode("app")}
              className="mt-1 h-4 w-4 accent-indigo-500"
            />
            <span>
              <span className="font-medium text-zinc-200">
                Use this app's invoice number
              </span>
              <span className="block text-xs text-zinc-500">
                Forces the QuickBooks number to match this app (may need "Custom
                transaction numbers" enabled in QuickBooks).
              </span>
            </span>
          </label>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-zinc-300">
          <input
            data-testid="qbo-reference-app-invoice"
            type="checkbox"
            checked={refApp}
            onChange={(e) => setRefApp(e.target.checked)}
            className="h-4 w-4 accent-indigo-500"
          />
          Reference this app's invoice number in the QuickBooks memo
        </label>
        <label className="mt-3 flex items-start gap-2 text-sm text-zinc-300">
          <input
            data-testid="qbo-ship-addr-from-job"
            type="checkbox"
            checked={shipAddr}
            onChange={(e) => setShipAddr(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-indigo-500"
          />
          <span>
            Use the job site address as the QuickBooks "Ship to" address
            <span className="block text-xs text-zinc-500">
              The customer's address goes to "Bill to"; the job address goes to "Ship to" so
              QuickBooks invoices show where the work was done.
            </span>
          </span>
        </label>
        <label className="mt-3 flex items-start gap-2 text-sm text-zinc-300">
          <input
            data-testid="qbo-material-in-description"
            type="checkbox"
            checked={materialDesc}
            onChange={(e) => setMaterialDesc(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-indigo-500"
          />
          <span>
            Add the material to each QuickBooks line description
            <span className="block text-xs text-zinc-500">
              Appends "— Material: Taj Mahal Quartzite 3cm" (from the quote or job) to every
              invoice line sent to QuickBooks.
            </span>
          </span>
        </label>
      </div>

      {/* Payment deposits */}
      <div className="mt-6 border-t border-zinc-800 pt-5">
        <h4 className="text-sm font-semibold text-zinc-200">Payment deposits</h4>
        <p className="mt-1 text-xs text-zinc-500">
          Where recorded payments are deposited in QuickBooks (e.g. your checking account or
          Undeposited Funds). Leave blank to use the QuickBooks default.
        </p>
        <div className="mt-3">
          {connected ? (
            <select
              data-testid="qbo-deposit-account"
              value={deposit.id || ""}
              onChange={(e) => {
                const a = depositAccounts.find((x) => String(x.id) === String(e.target.value));
                setDeposit({ id: e.target.value, name: a?.name || "" });
              }}
              className={`${selectCls} max-w-md`}
            >
              <option value="">— QuickBooks default —</option>
              {depositAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.type ? ` (${a.type})` : ""}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-zinc-400" data-testid="qbo-deposit-account">
              {deposit.name || "QuickBooks default"}
            </span>
          )}
        </div>
      </div>

      {/* Tax */}
      <div className="mt-6 border-t border-zinc-800 pt-5">
        <h4 className="text-sm font-semibold text-zinc-200">Sales tax</h4>
        <p className="mt-1 text-xs text-zinc-500">
          Your invoice totals already include tax, so leave this off unless your accountant
          wants QuickBooks to recalculate it.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {connected ? (
            <select
              data-testid="qbo-map-tax-code"
              value={tax.code_id || ""}
              onChange={(e) => {
                const t = taxcodes.find((x) => String(x.id) === String(e.target.value));
                setTax((p) => ({ ...p, code_id: e.target.value, code_name: t?.name || "" }));
              }}
              className={`${selectCls} max-w-xs`}
            >
              <option value="">— No tax code —</option>
              {taxcodes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-zinc-400" data-testid="qbo-map-tax-code">
              {tax.code_name || "No tax code"}
            </span>
          )}
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input
              data-testid="qbo-map-tax-apply"
              type="checkbox"
              checked={!!tax.apply}
              onChange={(e) => setTax((p) => ({ ...p, apply: e.target.checked }))}
              className="h-4 w-4 accent-indigo-500"
            />
            Apply this tax code in QuickBooks
          </label>
        </div>
      </div>

      <p className="mt-6 text-xs text-zinc-500" data-testid="qbo-map-customers-moved">
        Customer matching now has its own tab above.
      </p>

      <div className="mt-6 flex justify-end">
        <Button
          data-testid="qbo-map-save"
          onClick={save}
          disabled={saving}
          className="gap-2 bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
        >
          <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save mapping"}
        </Button>
      </div>
    </div>
  );
};
